// @flow
import { t } from '@lingui/macro';
import { US_ROVER_SUPPORT_NUMBER } from '@rover/react-lib/constants/phoneNumbers.constants';
import { createDuck } from '@rover/react-lib/lib/redux-duck';
import type {
  ActionCreator,
  EffectCreator,
  SelectorCreator,
  DuckOptions,
  Duck,
} from '@rover/react-lib/lib/redux-duck';

import { selectors as creditCardSelectors } from '@rover/react-lib/redux/ducks/creditCards.duck';
import { selectors as countryConfigurationSelectors } from '@rover/react-lib/redux/ducks/countryConfiguration.duck';
import {
  effects as currentUserEffects,
  selectors as currentUserSelectors,
} from '@rover/react-lib/redux/ducks/currentUser.duck';
import { effects as addressEffects } from '@rover/react-lib/redux/ducks/address.duck';

export type State = {
  selectedCardId: string,
  errorMessage: string,
  loading: boolean,
  serviceType: string,
};

type Actions = {
  setSelectedCardId: ActionCreator<string>,
  selectCardPending: ActionCreator<void>,
  selectCardSuccess: ActionCreator<{ selectedCardId: string }>,
  selectCardFailure: ActionCreator<{ errorMessage: string }>,
  setServiceType: ActionCreator<string>,
};

type Effects = {
  selectCard: EffectCreator<string>,
  prefetchData: EffectCreator<void>,
};

type Selectors = {
  getSelectedCardId: SelectorCreator<string>,
  getErrorMessage: SelectorCreator<string>,
  getLoading: SelectorCreator<boolean>,
  getServiceType: ActionCreator<string>,
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'conversationsCheckoutPage',
  initialState: {
    selectedCardId: '',
    errorMessage: '',
    loading: false,
    serviceType: '',
  },
  actions: {
    setSelectedCardId: selectedCardId => state => ({
      ...state,
      selectedCardId,
    }),
    selectCardPending: () => state => ({
      ...state,
      errorMessage: '',
      loading: true,
    }),
    selectCardSuccess: ({ selectedCardId }) => state => ({
      ...state,
      selectedCardId,
      errorMessage: '',
      loading: false,
    }),
    selectCardFailure: ({ errorMessage }) => state => ({
      ...state,
      errorMessage,
      loading: false,
    }),
    setServiceType: serviceType => state => ({
      ...state,
      serviceType,
    }),
  },
  effects: {
    selectCard: selectedCardId => async (dispatch, getState, duckActions): Promise<void> => {
      // If this form is not syncing with a Backbone form that is also
      // on the page, short circuit the rest of the logic here.
      if (!document.querySelector('[name="cc"]:not([disabled])')) {
        dispatch(duckActions.selectCardSuccess({ selectedCardId }));
        return;
      }

      dispatch(duckActions.selectCardPending());
      // NOTE(Ryan): fallback # is copied from ContactRoverSupoortSection.jsx
      const supportNumber =
        countryConfigurationSelectors.getCurrentCustomerSupportPhoneFormatted(getState()) ||
        US_ROVER_SUPPORT_NUMBER;

      try {
        // attempt to find backbone radio button,
        // we need to retry in case the backbone collection/view is fetching/rerendering
        let backboneRadioButton;
        let retry = 0;
        const maxRetries = 20;
        const retryInterval = 500;
        while (!backboneRadioButton && retry < maxRetries) {
          backboneRadioButton = document.getElementById(selectedCardId);
          if (backboneRadioButton) break;
          // NOTE(Ryan) no-await-in-loop is meant to remind you to parallelize promise calls,
          //            and we don't want that here
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => setTimeout(resolve, retryInterval));
          retry += 1;
        }

        // NOTE(Ryan) instanceof check is to make flow happy
        if (!backboneRadioButton || !(backboneRadioButton instanceof HTMLInputElement)) {
          // eslint-disable-next-line no-console
          console.error(
            `Cannot find Backbone radio button ID while selecting card: ${selectedCardId}`
          );
          throw new Error();
        }

        // attept to click backbone radio button
        backboneRadioButton.click();

        // confirm we have selected the backbone radio button
        if (!backboneRadioButton.checked) {
          // eslint-disable-next-line no-console
          console.error(
            `Failed to click/select Backbone radio button ID while selecting card: ${selectedCardId}`
          );
          throw new Error();
        }
        // update selectedCardId state
        // NOTE(Ryan) timeout here is to prevent "stuck loading spinner" bug
        await new Promise(resolve => setTimeout(resolve, 0));
        dispatch(duckActions.selectCardSuccess({ selectedCardId }));
      } catch (error) {
        const errorMessage = t`Error selecting card: please refresh the page and try again.  If the problem persists, please contact Customer Support at ${supportNumber}`;
        dispatch(duckActions.selectCardFailure({ errorMessage }));
      }
    },
    prefetchData: () => async (dispatch, getState) => {
      await dispatch(currentUserEffects.fetchCurrentUser());
      const personOpk = currentUserSelectors.getOpk(getState());
      await dispatch(addressEffects.fetchAddress(personOpk));
    },
  },
  selectors: {
    getSelectedCardId: (getState, createSelector) =>
      createSelector(
        [getState],
        state => state.selectedCardId
      ),
    getErrorMessage: (getState, createSelector) =>
      createSelector(
        [getState],
        state => state.errorMessage
      ),
    getLoading: (getState, createSelector) =>
      createSelector(
        [getState, creditCardSelectors.getLoading],
        ({ loading }, creditCardsLoading) => loading || creditCardsLoading
      ),
    getServiceType: (getState, createSelector) =>
      createSelector(
        [getState],
        state => state.serviceType
      ),
  },
};

export const { actions, selectors, effects, reducer } = (createDuck(options): Duck<
  State,
  Actions,
  Effects,
  Selectors
>);



// WEBPACK FOOTER //
// ../react-lib/src/pages/conversations/ConversationsCheckoutPage/ConversationsCheckoutPage.duck.jsx